import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { DateString } from 'routes/HumanResourcesModule/business/types';
import { LIGHTGRAY_HEADER_CLASS } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import { DEFAULT_AMOUNT_COLUMN } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import {
    SIMULATED_AMOUNT,
    HISTORIC_AMOUNT,
    PAY_COMPONENT,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { t } from 'i18next';
import { currencyFormatter } from 'helpers/formatHelper';
import { getLastYearDateString } from 'routes/HumanResourcesModule/business/helpers';
import { SimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import {
    createRealizedColumn,
    createSimulatedColumn,
} from 'routes/HumanResourcesModule/Simulation/helpers/gridHelpers';
import { PREVISIONAL_SIMULATED } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/helpers/constants';

// Function to create the custom realized column in the simulation result grid
export const createRealizedResultColumn = (
    date: DateString,
    startDate?: SimulationDateString,
): ColDef => ({
    ...DEFAULT_AMOUNT_COLUMN,
    headerName: t('humanResources.simulation.realized'),
    field: `${HISTORIC_AMOUNT}..${date}`,
    cellStyle: {
        color: 'lightgray',
    },
    cellRendererSelector: (params: ICellRendererParams) =>
        params.node.rowPinned
            ? { component: 'bottomPinnedRowRenderer' }
            : { component: 'customResultColumnCellRenderer' },
    cellRendererParams: {
        date,
        currentField: HISTORIC_AMOUNT,
        fieldToCompare: SIMULATED_AMOUNT,
        isEmployerCost: false,
        isMonthly: true,
        startDate,
    },
    headerClass: LIGHTGRAY_HEADER_CLASS,
});

export const createSimulatedEmployerCostsColumn = (
    date: DateString,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    startDate: SimulationDateString,
): ColDef => ({
    ...createSimulatedColumn(date),
    headerName: t('humanResources.simulation.employerCosts'),
    field: `CP_${SIMULATED_AMOUNT}..${date}`,
    valueGetter: (params) =>
        params?.data?.[PAY_COMPONENT] === PayComponent.EMPLOYER_COSTS
            ? params?.data[`${SIMULATED_AMOUNT}..${date}`]
            : 0,
    valueFormatter: (params) =>
        // If params.value is null or equal to 0 we return the dash
        !params.value ? '-' : currencyFormatter(params.value),
});

export const createBudgetPrevisionalSimulatedResultColumn = (
    date: DateString,
    startDate: SimulationDateString,
): ColDef => ({
    ...DEFAULT_AMOUNT_COLUMN,
    headerName: t('humanResources.simulation.nextYearPrevisionalSimulated'),
    field: `${PREVISIONAL_SIMULATED}..${date}`,
    cellRendererSelector: (params: ICellRendererParams) =>
        params.node.rowPinned
            ? { component: 'bottomPinnedRowRenderer' }
            : { component: 'customResultColumnCellRenderer' },
    cellRendererParams: {
        date,
        isMonthly: false,
        currentField: `${PREVISIONAL_SIMULATED}..${date}`,
        fieldToCompare: `${PREVISIONAL_SIMULATED}..${getLastYearDateString(
            date,
        )}`,
        startDate,
    },
});

// Function to create the custom realized employer cost column in the result grid
export const createRealizedResultEmployerCostsColumn = (
    date: DateString,
    startDate: SimulationDateString,
): ColDef => ({
    ...createRealizedResultColumn(date),
    headerName: t('humanResources.simulation.employerCosts'),
    field: `CP_${HISTORIC_AMOUNT}..${date}`,
    cellStyle: {
        color: 'lightgray',
    },
    valueGetter: (params) =>
        params?.data?.[PAY_COMPONENT] === PayComponent.EMPLOYER_COSTS
            ? params?.data[`${HISTORIC_AMOUNT}..${date}`]
            : 0,
    valueFormatter: (params) =>
        // If params.value is null or equal to 0 we return the dash
        !params.value ? '-' : currencyFormatter(params.value),
    cellRendererParams: {
        date,
        currentField: `CP_${HISTORIC_AMOUNT}`,
        fieldToCompare: `CP_${SIMULATED_AMOUNT}`,
        isEmployerCost: true,
        isMonthly: true,
        startDate,
    },
    headerClass: LIGHTGRAY_HEADER_CLASS,
});

// Functions to create the custom realized column " Simulé prévisionel" in the simulation result grid
export const createPrevisionalSimulatedResultColumn = (
    date: DateString,
    startDate: SimulationDateString,
): ColDef => ({
    ...DEFAULT_AMOUNT_COLUMN,
    headerName: t(
        `humanResources.simulation.${date.slice(0, 4) === startDate.slice(0, 4) ? 'previsionalSimulated' : 'nextYearPrevisionalSimulated'}`,
    ),

    field: `${PREVISIONAL_SIMULATED}..${date}`,
    cellRendererSelector: (params: ICellRendererParams) =>
        params.node.rowPinned
            ? { component: 'bottomPinnedRowRenderer' }
            : { component: 'customResultColumnCellRenderer' },
    cellRendererParams: {
        date,
        isMonthly: false,
        currentField: `${PREVISIONAL_SIMULATED}..${date}`,
        fieldToCompare: `${HISTORIC_AMOUNT}..${getLastYearDateString(date)}`,
        startDate,
    },
});

export const createRealizedEmployerCostsColumn = (
    date: DateString,
): ColDef => ({
    ...createRealizedColumn(date),
    headerName: t('humanResources.simulation.employerCosts'),
    field: '',

    cellStyle: {
        color: 'lightgray',
    },
    valueGetter: (params) =>
        params?.data?.[PAY_COMPONENT] === PayComponent.EMPLOYER_COSTS
            ? params?.data[`${HISTORIC_AMOUNT}..${date}`]
            : 0,
    valueFormatter: (params) =>
        // If params.value is null or equal to 0 we return the dash
        !params.value ? '-' : currencyFormatter(params.value),
});
